import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { CheckboxGroupPresentationProps, CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import { DatePickerPresentationProps } from "@insite/mobius/DatePicker";
import { FormFieldPresentationProps, FormFieldPropsMock } from "@insite/mobius/FormField/FormField";
import { RadioProps } from "@insite/mobius/Radio";
import { RadioGroupProps } from "@insite/mobius/RadioGroup";
import { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { getProp } from "@insite/mobius/utilities";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import { colorPalette, colorStrings, fontFamily, fontFamilyAlt } from "@waytek/Utilities/variables";
import { css } from "styled-components";

export const formField: { defaultProps?: FormFieldPresentationProps<FormFieldPropsMock> } = {
    defaultProps: {
        sizeVariant: "small",
        errorProps: {
            color: "#F00",
            size: "12px",
            weight: 400,
        },
        hintProps: {
            fontFamily: fontFamilyAlt,
            color: colorStrings.commonGlobalSecondaryGray,
            size: "12px",
            weight: 400,
        },
    },
};

export const textField: {
    defaultProps?: TextFieldPresentationProps;
} = {
    defaultProps: {
        mergeCss: true,
        cssOverrides: {
            inputSelect: css`
                &:not(:disabled) {
                    &:not(.react-datetime-picker__inputGroup__input) {
                        border-width: 1px;

                        &:focus {
                            border-width: 1px;
                        }
                    }

                    &.react-datetime-picker {
                        padding: 0 0 0 12px;

                        &:focus {
                            padding: 0 0 0 12px;
                        }

                        .date-picker__calendar {
                            margin: 0;
                        }
                    }
                }
            `,
        },
    },
};

export const checkbox: {
    defaultProps?: CheckboxPresentationProps;
    groupDefaultProps?: FieldSetGroupPresentationProps<CheckboxGroupProps> & CheckboxGroupPresentationProps;
} = {
    defaultProps: {
        mergeCss: true,
        color: colorStrings.commonGlobalWhite,
        iconProps: {
            mergeCss: true,
            size: 20,
            css: css`
                svg {
                    stroke-width: 4;
                }
            `,
        },
        indeterminateColor: colorStrings.commonGlobalWhite,
        indeterminateIconProps: {
            size: 20,
        },
        typographyProps: {
            fontFamily: fontFamilyAlt,
            size: "14px",
            disabledColor: colorStrings.commonGlobalDisabledLabel,
        },
        css: css`
            span[role="checkbox"] {
                /* stylelint-disable-next-line no-duplicate-selectors */
                & {
                    border-radius: 0;
                    border: 1px solid ${colorPalette.common.border};
                    height: 20px;
                    width: 20px;
                    min-width: 20px;

                    &:focus {
                        box-shadow: none;
                    }
                }

                &[aria-checked="true"]:not([aria-disabled="true"]),
                &[aria-checked="mixed"]:not([aria-disabled="true"]) {
                    border: 1px solid ${colorPalette.secondary.main};
                    color: ${colorPalette.secondary.main};
                }

                &[aria-disabled="true"] {
                    background-color: ${colorPalette.common.global.disabledBg};
                    border: 1px solid ${colorPalette.common.border};
                }
            }
        `,
    },
};

export const radio: {
    defaultProps?: FieldSetPresentationProps<RadioProps>;
    groupDefaultProps?: FieldSetGroupPresentationProps<RadioGroupProps>;
} = {
    defaultProps: {
        mergeCss: true,
        color: colorStrings.commonGlobalWhite,
        typographyProps: {
            fontFamily: fontFamilyAlt,
            size: "14px",
            disabledColor: colorStrings.commonGlobalDisabledLabel,
        },
        css: css`
            input {
                width: 13px;
                height: 13px;
                margin-top: 0;

                & + label {
                    cursor: pointer;
                    transition: all ease-in-out ${getProp("theme.transition.duration.regular")}ms;
                }

                &:not(:disabled) {
                    & + label {
                        &::before {
                            border: 1px solid ${colorPalette.common.border};
                            background-color: ${colorPalette.common.global.white};
                            height: 13px;
                            width: 13px;
                            flex-shrink: 0;
                        }

                        &::after {
                            background: ${colorPalette.primary.main};
                            height: 13px;
                            width: 13px;
                            transform: scale(0.7);
                            left: 0;
                            top: initial;
                        }
                    }

                    &:checked + label {
                        &::before {
                            border: 1px solid ${colorPalette.common.global.guardianBlue};
                        }
                    }
                }

                :disabled {
                    & + label {
                        &::before {
                            border: 1px solid ${colorPalette.common.border};
                            background-color: ${colorPalette.common.global.disabledBg};
                            height: 13px;
                            width: 13px;
                        }

                        &::after {
                            background: ${colorPalette.common.border};
                            height: 13px;
                            width: 13px;
                            transform: scale(0.7);
                            left: 0;
                            top: initial;
                        }
                    }

                    &:checked + label {
                        &::before {
                            border: 1px solid ${colorPalette.common.border};
                        }

                        &::after {
                            background: ${colorPalette.common.border};
                        }
                    }
                }
            }
        `,
    },
};

export const select: {
    defaultProps?: FormFieldPresentationProps<SelectProps> & SelectPresentationProps;
} = {
    defaultProps: {
        iconProps: {
            color: colorStrings.commonGlobalGuardianBlue,
        },
    },
};

export const datePicker: {
    defaultProps?: DatePickerPresentationProps;
} = {
    defaultProps: {
        sizeVariant: "small",
        backgroundColor: colorStrings.commonGlobalGuardianBlue,
        calendarIconProps: {
            color: colorStrings.commonGlobalWhite,
        },
        cssOverrides: {
            datePicker: css`
                .react-calendar {
                    button {
                        font-family: ${fontFamily};

                        &:enabled {
                            &:hover:not(:disabled),
                            &:focus:not(:disabled) {
                                color: ${colorPalette.common.global.guardianBlue};
                                background-color: ${colorPalette.common.global.extraLight};
                            }
                        }
                    }

                    &__month-view {
                        &__days {
                            &__day {
                                padding: 11px 0 10px;
                            }
                        }
                    }

                    &__tile {
                        color: ${colorPalette.common.global.secondaryGray};

                        &--hasActive:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth),
                        &--active:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth) {
                            background-color: ${colorPalette.common.global.guardianBlue};
                            color: ${colorPalette.common.global.white};
                        }
                        &--now {
                            color: ${colorPalette.common.global.guardianBlue};
                        }
                    }
                }
            `,
        },
    },
};
